import { defineStore } from 'pinia'
import { watch } from 'vue'
import { uuid } from '../helpers'
import { appStore } from './app'
import { mqttStore } from './mqtt'
import AgoraRTC from 'agora-rtc-sdk-ng'
import type { IAgoraRTCClient, IAgoraRTCRemoteUser, IRemoteVideoTrack } from 'agora-rtc-sdk-ng'
import type { Telemetry } from '../types/dashboard.types'
import log from '@/utils/useLogger'

// Module-scoped state for the zoom telemetry sync. Kept out of the Pinia
// state object because it's bookkeeping for a side-effecting watcher, not
// reactive UI state.
//
// `zoomTelemetryWatcherInstalled` guards installation so the watcher is
// set up exactly once for the lifetime of the page. `lastZoomCommandTs`
// is bumped at the top of every updateZoom() call; the telemetry watcher
// only writes `zoomAmnt` from telemetry when at least ZOOM_TELEMETRY_QUIET_MS
// have elapsed since that timestamp, so an in-flight zoom motor reading
// an intermediate ratio never clobbers a value the user just commanded.
let zoomTelemetryWatcherInstalled = false
let lastZoomCommandTs = 0
const ZOOM_TELEMETRY_QUIET_MS = 2000

const parseExposureCompensation = (raw: unknown): number => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0.0
  return (
    parseFloat(
      String(raw ?? '')
        .replace('NEG_', '-')
        .replace('POS_', '')
        .replace('P', '.')
        .replace('EV', ''),
    ) || 0.0
  )
}

const parseIso = (raw: unknown): number => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 100
  return parseInt(String(raw ?? '').replace('ISO_', ''), 10) || 100
}

const parseShutterSpeed = (raw: unknown): number => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 10
  return parseInt(String(raw ?? '').replace('SHUTTER_SPEED1_', ''), 10) || 10
}

interface Stream {
  user: IAgoraRTCRemoteUser | null
  stream_for: string
  stream_channel: string
  stream_token: string
  device_name: string
  type: string
  label: string
  id: number
  onStatus: boolean
  containers: HTMLElement[]
  client?: IAgoraRTCClient // Store the client instance
}

interface GimbalControls {
  tiltVal: number | string | null
  rollVal: number | string | null
  panVal: number | string | null
  [key: string]: any
}

interface GimbalIgnore {
  tilt: boolean
  pan: boolean
  roll: boolean
}

interface ExposureValues {
  exposure: number | null
  iso: number | null
  shutterSpeed: number | null
}

interface ThermalValues {
  palette: string | null
  gain: string | null
  threshold: string | null
}

interface FocusValues {
  selectedMode: string | null
  ringValue: number | null
  targetHeight: number | null
  targetWidth: number | null
}

interface Point {
  x: number
  y: number
}

const getDefaultState = () => ({
  streamTracks: [],
  user: null,
  stream: null,
})

export const streamStore = defineStore('stream', {
  state: () => ({
    streamTracks: [] as IRemoteVideoTrack[],
    isManualControl: false,
    allStreams: [
      {
        user: null,
        stream_for: 'dock',
        stream_channel: '',
        stream_token: '',
        device_name: '',
        type: 'stream1',
        label: 'Dock Cam',
        id: Number(uuid(6)),
        onStatus: false,
        containers: [],
      },
      {
        user: null,
        stream_for: 'uav',
        stream_channel: '',
        stream_token: '',
        device_name: '',
        type: 'stream1',
        label: 'UAV Cam',
        id: Number(uuid(6)),
        onStatus: true,
        containers: [],
        client: undefined,
      },
    ] as Stream[],
    isUavStreamOn: true,
    isDockStreamOn: false,
    isStream2Collapsed: true,
    cameraMode: 'PHOTO',
    exposureMode: 'P',
    exposureValues: {
      exposure: null,
      iso: null,
      shutterSpeed: null,
    } as ExposureValues,
    isRecording: false,
    activeViewMap: null as string | null,
    cameraState: null as any,
    gamepad: null as any,
    activeLens: null as string | null,
    isSwitching: false,
    zoomAmnt: null as number | null,
    gimbalControls: {
      tiltVal: null,
      rollVal: null,
      panVal: null,
    } as GimbalControls,
    gimbalIgnore: {
      tilt: true,
      pan: true,
      roll: true,
    } as GimbalIgnore,
    thermalValues: {
      palette: null,
      gain: null,
      threshold: null,
    } as ThermalValues,
    thermalGainModes: ['AUTO', 'LOW', 'HIGH', 'SUPER_CLEAR'],
    thermalTempModes: ['DISABLED', 'SPOT', 'REGION', 'CENTRAL_POINT_METERING'],
    focusOptions: ['MANUAL', 'AF', 'AFC', 'FINE_TUNE'],
    focusValues: {
      selectedMode: null,
      ringValue: null,
      targetHeight: null,
      targetWidth: null,
    } as FocusValues,
    feedContainer: null as HTMLElement | null,
    feedSource: null as any,
    // Which feed pane currently receives mouse-based camera controls.
    // 'uav' is the only one with a gimbal today; dock/slinktech kept for future.
    activeFeedId: 'uav' as 'uav' | 'dock' | 'slinktech',
  }),

  actions: {
    resetStreamForDevice(streamFor: 'uav' | 'dock' | 'slinktech'): void {
      const stream = this.allStreams.find((s) => s.stream_for === streamFor)
      if (stream) {
        stream.stream_token = ''
        stream.stream_channel = ''
        stream.onStatus = false
        if (stream.client) {
          stream.client = undefined
        }
        if (stream.user) {
          stream.user = null
        }
      }
    },
    /**
     * Device-group switch entry point for a given stream lane.
     *
     * Critical: the prior agora connection must be fully torn down BEFORE we
     * drop the client/token references, otherwise `leave()` can't find a
     * client to call `client.leave()` on and the old channel is orphaned
     * (no STOP_STREAM_* MQTT, containers never cleared, new channel fails to
     * attach). `resetStreamForDevice` alone does not achieve this because it
     * just nulls the refs synchronously.
     *
     * Awaited from `MapLayout.selectedDevice` watcher + `onUnmounted`. The
     * single-source-of-truth pattern means `Stream.vue` no longer needs to
     * call `leave()` itself on device change.
     */
    async leaveAndResetStreamForDevice(streamFor: 'uav' | 'dock' | 'slinktech'): Promise<void> {
      const stream = this.allStreams.find((s) => s.stream_for === streamFor)
      if (!stream) return
      if (stream.client) {
        try {
          await this.leave(stream as unknown as Stream)
        } catch (err) {
          log.error(`leaveAndResetStreamForDevice leave error (${streamFor})`, err)
        }
      }
      // Clear cached device-scoped config so the next `loadAllStreamConfig`
      // round fills it with fresh credentials for the new device.
      stream.stream_token = ''
      stream.stream_channel = ''
      stream.device_name = ''
      stream.onStatus = false
      stream.user = null
      stream.client = undefined
    },
    addSlinkTechStream(): number {
      // Check if slinktech stream already exists
      const existingIndex = this.allStreams.findIndex((s) => s.stream_for === 'slinktech')
      if (existingIndex !== -1) {
        return existingIndex
      }
      // Add new slinktech stream
      this.allStreams.push({
        user: null,
        stream_for: 'slinktech',
        stream_channel: '',
        stream_token: '',
        device_name: '',
        type: 'stream1',
        label: 'SlinkTech',
        id: Number(uuid(6)),
        onStatus: false,
        containers: [],
        client: undefined,
      })
      return this.allStreams.length - 1
    },
    async join(view: HTMLElement, stream: Stream): Promise<IAgoraRTCClient | undefined> {
      const { selectedRC, selectedDock, selectedPortal } = appStore()
      const { sendCommand } = mqttStore()
      // If the container already exists in the stream, just play the video track without rejoining
      if (stream.client && stream.containers.includes(view) && stream.user?.videoTrack) {
        try {
          stream.user.videoTrack.play(view)
          return stream.client
        } catch (error) {
          log.error('Error playing existing video track:', error)
        }
      }

      // Use existing client if available
      if (stream.client) {
        if (!stream.containers.includes(view)) {
          stream.containers.push(view)
        }
        if (stream.user?.videoTrack) {
          try {
            stream.user.videoTrack.play(view)
            return stream.client
          } catch (error) {
            log.error('Error playing video with existing client:', error)
          }
        }
        return stream.client
      }

      // Create new client if none exists
      const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' })
      stream.client = client // Store client in stream
      AgoraRTC.setLogLevel(4)

      const currentDevice =
        stream.stream_for === 'uav'
          ? selectedRC
          : stream.stream_for === 'dock'
            ? selectedDock
            : selectedPortal

      if (!stream.stream_channel) stream.stream_channel = currentDevice
      if (!stream.device_name) stream.device_name = currentDevice

      // Don't attempt to join if token is not available
      if (!stream.stream_token) {
        log.warn('Stream token not available yet. Please wait for token to be loaded.')
        stream.client = undefined
        return undefined
      }

      // Verify the stream token is for the current device
      if (stream.device_name !== currentDevice) {
        log.warn(
          `Stream device mismatch. Expected: ${currentDevice}, Got: ${stream.device_name}. Waiting for new config.`,
        )
        stream.client = undefined
        return undefined
      }

      try {
        // const topic = stream.stream_for === 'uav' ? `START_STREAM_RC` : `START_STREAM_DOCK`
        const topic =
          stream.stream_for === 'uav'
            ? `START_STREAM_RC`
            : stream.stream_for === 'dock'
              ? `START_STREAM_DOCK`
              : `START_STREAM_SLINKTECH`
        await sendCommand(topic, {})
        await client.join(
          import.meta.env.VITE_APP_AGORA_APP_ID,
          stream.stream_channel,
          stream.stream_token,
          0,
        )

        client.on('user-published', async (user, mediaType) => {
          try {
            await client.subscribe(user, mediaType)

            if (!stream.onStatus) stream.onStatus = true

            if (mediaType === 'video') {
              const remoteVideoTrack = user.videoTrack
              stream.user = user

              if (!stream.containers.includes(view)) {
                stream.containers.push(view)
              }

              stream.containers.forEach((container: HTMLElement) => {
                if (remoteVideoTrack) {
                  remoteVideoTrack.play(container)
                  if (!this.streamTracks.includes(remoteVideoTrack)) {
                    this.streamTracks.push(remoteVideoTrack)
                  }
                }
              })
            }
            if (mediaType === 'audio' && user.audioTrack) {
              user.audioTrack.play()
            }
          } catch (err) {
            log.error('Error subscribing to stream:', err)
          }
        })
        log.info('Successfully joined the Agora channel')
        return client
      } catch (err) {
        log.error('Error joining channel:', err)
        stream.client = undefined // Reset client on failure
        return undefined
      }
    },
    async leave(stream: Stream): Promise<void> {
      const { sendCommand } = mqttStore()
      const client = stream.client

      if (!client) return

      if (stream.user) {
        if (stream.user.videoTrack) {
          stream.user.videoTrack.stop()
          ;(stream.user.videoTrack as any).close()
        }
        stream.user = null
      }

      try {
        const topic =
          stream.stream_for === 'uav'
            ? `STOP_STREAM_RC`
            : stream.stream_for === 'dock'
              ? `STOP_STREAM_DOCK`
              : `STOP_STREAM_SLINKTECH`
        await sendCommand(topic, {})
        await client.leave()

        stream.containers.forEach((el: HTMLElement) => {
          el.innerHTML = ''
        })
        stream.containers = []
        stream.client = undefined // Clear client reference
        stream.onStatus = false
        log.info('Successfully left the Agora channel')
      } catch (error) {
        log.error('Error leaving the channel:', error)
      }
    },
    reset() {
      Object.assign(this.$state, getDefaultState())
    },

    setActiveFeed(id: 'uav' | 'dock' | 'slinktech') {
      this.activeFeedId = id
    },

    shoot() {
      try {
        const { cameraControl } = mqttStore()

        if (this.cameraMode === 'VIDEO') {
          if (!this.isRecording) {
            this.isRecording = true
            cameraControl('CAMERA_RECORD', 'record_command', 'START_RECORD')
          } else {
            this.isRecording = false
            cameraControl('CAMERA_RECORD', 'record_command', 'STOP_RECORD')
          }
        } else if (this.cameraMode == 'PHOTO') {
          cameraControl('CAMERA_PHOTO', 'photo_command', 'TAKE_PHOTO')
        }
      } catch (err) {
        log.error('Error in shoot function:', err)
      }
    },

    initializeExposure() {
      try {
        const useAppStore = appStore()
        const telemetry = useAppStore.telemetry as Telemetry
        const settings = telemetry?.gimbalCamera?.camera?.settings

        this.exposureValues.exposure = parseExposureCompensation(settings?.exposureCompensation)
        this.exposureValues.iso = parseIso(settings?.iso)
        this.exposureValues.shutterSpeed = parseShutterSpeed(settings?.shutterSpeed)
      } catch (err) {
        log.error('Error in initializeExposure function:', err)
      }
    },

    initializeGimbal() {
      this.resetAll()
    },

    initializeZoom() {
      try {
        const useAppStore = appStore()
        const telemetry = useAppStore.telemetry

        this.zoomAmnt = Number(telemetry?.gimbalCamera?.camera?.zoomRatios) || 1
        // Install the long-lived telemetry watcher on first init so the
        // wheel-driven UI no longer relies on per-command setTimeout()
        // calls to pull hardware-reported zoom back in. See the
        // declaration of `zoomTelemetryWatcherInstalled` above for why
        // the bookkeeping lives at module scope.
        this._installZoomTelemetrySync()
      } catch (err) {
        log.error('Error in initializeZoom function:', err)
      }
    },

    // Long-lived watcher on telemetry-reported zoom. Updates `zoomAmnt`
    // only when no user-issued zoom command has happened in the last
    // ZOOM_TELEMETRY_QUIET_MS — that's what lets the hardware finish a
    // commanded zoom transition (the motor reports intermediate ratios
    // mid-travel on the H20T) before we trust telemetry again. Replaces
    // the old `setTimeout(() => this.initializeZoom(), 5000)` chain
    // inside updateZoom(), which leaked one timeout per scroll notch
    // and clobbered freshly commanded values whenever they finally
    // fired in batches.
    _installZoomTelemetrySync() {
      if (zoomTelemetryWatcherInstalled) return
      zoomTelemetryWatcherInstalled = true
      const useAppStore = appStore()
      watch(
        () => useAppStore.telemetry?.gimbalCamera?.camera?.zoomRatios,
        (raw) => {
          const z = Number(raw)
          if (!Number.isFinite(z) || z <= 0) return
          if (Date.now() - lastZoomCommandTs < ZOOM_TELEMETRY_QUIET_MS) return
          if (this.zoomAmnt === z) return
          this.zoomAmnt = z
        },
      )
    },

    initializeThermal() {
      try {
        const useAppStore = appStore()
        const telemetry = useAppStore.telemetry

        this.thermalValues.palette = telemetry?.gimbalCamera?.camera?.thermal?.palette || ''
        this.thermalValues.gain = telemetry?.gimbalCamera?.camera.thermal.gainMode || ''
        this.thermalValues.threshold =
          telemetry?.gimbalCamera?.camera?.thermal.temperatureMeasureMode || ''
      } catch (err) {
        log.error('Error in initializeThermal function:', err)
      }
    },

    initializeFocus() {
      const useAppStore = appStore()
      const telemetry = useAppStore.telemetry as Telemetry

      this.focusValues.selectedMode = telemetry?.gimbalCamera?.camera?.focus?.mode || 'AFC'
      this.focusValues.ringValue = telemetry?.gimbalCamera?.camera?.focus?.ringValue || 0
      this.focusValues.targetHeight = telemetry?.gimbalCamera?.camera?.focus?.target?.y || 0
      this.focusValues.targetWidth = telemetry?.gimbalCamera?.camera?.focus?.target?.x || 0
    },

    initializeActiveLens() {
      const { telemetry } = appStore() as { telemetry: Telemetry }
      const cameraNames: Record<string, string> = {
        WIDE_CAMERA: 'W',
        INFRARED_CAMERA: 'T',
        ZOOM_CAMERA: 'Z',
      }

      this.activeLens =
        cameraNames[telemetry?.gimbalCamera?.camera?.videoStreamSourceType || ''] || 'W'
    },

    mapScale(val: any, inMin: any, inMax: any, outMin: any, outMax: any) {
      try {
        return ((val - inMin) / (inMax - inMin)) * (outMax - outMin) + outMin
      } catch (err) {
        log.error('Error in mapScale function:', err)
        return 0
      }
    },

    getClickCoords(event: MouseEvent) {
      try {
        if (this.focusValues.selectedMode === 'AF' && this.feedContainer) {
          const screen = this.feedContainer.getBoundingClientRect()

          const xCoord = this.mapScale(event.clientX - screen.left, 0, screen.width, 0, 1)
          const yCoord = this.mapScale(event.clientY - screen.top, 0, screen.height, 0, 1)

          this.updateAutoFocus({ x: xCoord, y: yCoord })
        }
      } catch (err) {
        log.error('Error in getClickCoords function:', err)
      }
    },
    async updateAutoFocus(obj: Point) {
      try {
        const { sendCommand } = mqttStore()

        const data = {
          autoFocusMode: 'AF',
          autoFocusTargetHeight: obj.y,
          autoFocusTargetWidth: obj.x,
        }

        await sendCommand('AUTO_FOCUS', data)
      } catch (err) {
        log.error('Error in updateAutoFocus function:', err)
      }
    },

    updateLens(val: string) {
      try {
        const { cameraControl } = mqttStore()

        switch (val) {
          case 'W':
            cameraControl('CAMERA_VIDEO_SOURCE', 'camera_video_source', 'WIDE_CAMERA')
            this.isSwitching = true
            break

          case 'Z':
            cameraControl('CAMERA_VIDEO_SOURCE', 'camera_video_source', 'ZOOM_CAMERA')
            this.isSwitching = true
            break
          case 'T':
            cameraControl('CAMERA_VIDEO_SOURCE', 'camera_video_source', 'INFRARED_CAMERA')
            this.isSwitching = true
            break
        }
      } catch (err) {
        log.error('Error in updateLens function:', err)
      }
    },

    async updateTilt(val: number, duration: number = 1) {
      try {
        const { updateGimbalCommand } = mqttStore()

        this.gimbalControls.tiltVal = val
        this.gimbalControls.mode = 'ABSOLUTE_ANGLE'
        this.gimbalControls.duration = duration

        this.gimbalIgnore.tilt = false
        this.gimbalIgnore.pan = true
        this.gimbalIgnore.roll = true
        await updateGimbalCommand(this.gimbalControls, this.gimbalIgnore)
      } catch (err) {
        log.error('Error in updateTilt function:', err)
      }
    },

    async updatePan(val: number, duration: number = 1) {
      try {
        const { updateGimbalCommand } = mqttStore()

        this.gimbalControls.panVal = val
        this.gimbalControls.mode = 'ABSOLUTE_ANGLE'
        this.gimbalControls.duration = duration
        this.gimbalIgnore.tilt = true
        this.gimbalIgnore.pan = false
        this.gimbalIgnore.roll = true

        await updateGimbalCommand(this.gimbalControls, this.gimbalIgnore)
      } catch (err) {
        log.error('Error in updatePan function:', err)
      }
    },

    async updateRoll(val: number) {
      try {
        const { updateGimbalCommand } = mqttStore()

        this.gimbalControls.rollVal = val
        this.gimbalControls.mode = 'ABSOLUTE_ANGLE'
        this.gimbalControls.duration = 1
        this.gimbalIgnore.tilt = true
        this.gimbalIgnore.pan = true
        this.gimbalIgnore.roll = false
        await updateGimbalCommand(this.gimbalControls, this.gimbalIgnore)
      } catch (err) {
        log.error('Error in updateRoll function:', err)
      }
    },

    async updateTiltPan(obj: any, duration: number = 1) {
      try {
        const { updateGimbalCommand } = mqttStore()

        this.gimbalControls.panVal = obj.x
        this.gimbalControls.tiltVal = obj.y
        this.gimbalControls.mode = 'RELATIVE_ANGLE'
        this.gimbalControls.duration = duration
        this.gimbalIgnore.pan = false
        this.gimbalIgnore.tilt = false
        this.gimbalIgnore.roll = true

        await updateGimbalCommand(this.gimbalControls, this.gimbalIgnore)
      } catch (err) {
        log.error('Error in updateTiltPan function:', err)
      }
    },

    async resetAll() {
      try {
        const { sendCommand } = mqttStore()
        await sendCommand('GIMBAL_RECENTER')
      } catch (err) {
        log.error('Error in resetAll function:', err)
      }
    },

    updateZoom(val: number) {
      try {
        const { cameraControl } = mqttStore()
        const { cameraConfig } = appStore()
        const zoomRange = cameraConfig?.CameraZoomRatiosRange || [1, 56]

        // Snap the incoming request to the closest supported ratio. We
        // intentionally do NOT bias the snap by direction-of-motion
        // anymore: callers (FeedOverlay wheel, controller, widget) are
        // now responsible for emitting in-range values, and the
        // direction bias was the source of the oscillation bug — it
        // compared an unsnapped `val` against the snapped `previousZoom`,
        // so during a wheel sweep the comparison flipped every tick and
        // zoom bounced between adjacent levels.
        let closestZoom = zoomRange[0]
        let minDifference = Math.abs(val - closestZoom)
        for (let i = 1; i < zoomRange.length; i++) {
          const zoom = zoomRange[i]
          const difference = Math.abs(val - zoom)
          if (difference < minDifference) {
            minDifference = difference
            closestZoom = zoom
          }
        }

        // Stamp the command timestamp BEFORE mutating state so the
        // telemetry watcher's quiet-window check sees a fresh value if
        // a telemetry tick lands in the same microtask.
        lastZoomCommandTs = Date.now()
        this.zoomAmnt = closestZoom
        cameraControl('CAMERA_ZOOM', 'zoom_ratio', this.zoomAmnt || 1)
        // The previous implementation scheduled `setTimeout(initializeZoom, 5000)`
        // on every call, accumulating timers that later clobbered
        // freshly-commanded zoom values. The long-lived telemetry
        // watcher installed by `_installZoomTelemetrySync` now handles
        // hardware reconciliation, debounced by ZOOM_TELEMETRY_QUIET_MS.
      } catch (err) {
        log.error('Error in updateZoom function:', err)
      }
    },

    toggleCamMode() {
      try {
        const { cameraControl } = mqttStore()

        let payload
        if (this.cameraMode === 'PHOTO') {
          this.cameraMode = 'VIDEO'
          payload = 'VIDEO_NORMAL'
        } else if (this.cameraMode === 'VIDEO') {
          this.cameraMode = 'PHOTO'
          payload = 'PHOTO_NORMAL'
        }
        cameraControl('CAMERA_MODE', 'camera_mode', payload || '')
      } catch (err) {
        log.error('Error in toggleCamMode function:', err)
      }
    },

    toggleFeed(container: HTMLElement, source: Stream): void {
      if (!source.onStatus) {
        this.join(container, source)
      } else {
        this.leave(source)
      }
    },
    refreshFeed(container: HTMLElement, source: Stream): void {
      const doRefresh = async () => {
        try {
          await this.leave(source)
          await this.join(container, source)
        } catch (error) {
          log.error('Error refreshing feed:', error)
        }
      }
      doRefresh()
    },
    updateContainer(stream: Stream, newContainer: HTMLElement): void {
      if (!stream.containers.includes(newContainer)) {
        stream.containers.push(newContainer)
      }

      // Check if stream is active and has a video track before playing
      if (stream.user?.videoTrack) {
        try {
          // Stop the track in the new container first to avoid duplicate playback
          stream.user.videoTrack.stop()
          // Play the track in the new container
          stream.user.videoTrack.play(newContainer)
        } catch (error) {
          log.error('Error playing video track in new container:', error)
        }
      } else {
        log.warn('No video track available to play in new container')
        this.join(newContainer, stream)
      }
    },
  },
})
